export function parseCSV(csvText: string): Record<string, string>[] {
  const rows = splitCSVRows(csvText);
  if (rows.length === 0) return [];

  const headers = parseCSVLine(rows[0]);
  const headerCount = headers.length;

  if (headerCount === 0) return [];

  const data: Record<string, string>[] = [];
  for (let i = 1; i < rows.length; i++) {
    const values = parseCSVLine(rows[i]);

    if (values.length === 0) continue;

    if (
      values.length !== headerCount &&
      values.length !== headerCount - 1 &&
      values.length !== headerCount + 1
    ) {
      console.warn(
        `Skipping row ${i + 1}: expected ${headerCount} columns, got ${values.length}. Row data: ${rows[i].substring(0, 100)}`
      );
      continue;
    }

    const normalizedValues = values.slice(0, headerCount);
    while (normalizedValues.length < headerCount) {
      normalizedValues.push('');
    }

    const hasData = normalizedValues.some((value) => value.trim() !== '');
    if (!hasData) continue;

    const row: Record<string, string> = {};
    headers.forEach((header, index) => {
      row[header] = normalizedValues[index]?.trim() || '';
    });

    const hasNonEmptyField = Object.values(row).some((value) => {
      const trimmed = value.trim();
      return trimmed !== '' && trimmed !== ',' && trimmed.length > 0;
    });

    const allFieldsEmpty = normalizedValues.every((value) => {
      const trimmed = value.trim();
      return trimmed === '' || trimmed === ',';
    });

    if (hasNonEmptyField && !allFieldsEmpty) {
      data.push(row);
    }
  }

  return data;
}

function splitCSVRows(csvText: string): string[] {
  const rows: string[] = [];
  let currentRow = '';
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentRow += '""';
        i++;
      } else {
        inQuotes = !inQuotes;
        currentRow += char;
      }
    } else if (char === '\n' && !inQuotes) {
      if (currentRow.trim()) {
        rows.push(currentRow);
      }
      currentRow = '';
    } else if (char === '\r') {
      if (nextChar !== '\n') {
        if (!inQuotes && currentRow.trim()) {
          rows.push(currentRow);
          currentRow = '';
        } else {
          currentRow += char;
        }
      }
    } else {
      currentRow += char;
    }
  }

  if (currentRow.trim()) {
    rows.push(currentRow);
  }

  return rows;
}

function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  result.push(current.trim());

  return result;
}

export function convertToCSV(data: Record<string, string>[]): string {
  if (data.length === 0) return '';

  const headers = new Set<string>();
  data.forEach((row) => {
    Object.keys(row).forEach((key) => headers.add(key));
  });

  const headerArray = Array.from(headers);

  const lines: string[] = [];

  lines.push(headerArray.map(escapeCSVField).join(','));

  data.forEach((row) => {
    const values = headerArray.map((header) =>
      escapeCSVField(row[header] || '')
    );
    lines.push(values.join(','));
  });

  return lines.join('\n');
}

function escapeCSVField(field: string): string {
  if (field.includes(',') || field.includes('"') || field.includes('\n')) {
    return `"${field.replace(/"/g, '""')}"`;
  }
  return field;
}
